import {
    type CamelCasedEducationExperience,
    educationExperienceRequiringOfficialTranscript,
    educationExperiencesThatCanRequireOfficial,
    unofficialTranscripts,
} from 'CareerProfiles';
import { getProgramConfigValue } from 'Program';
import {
    type CurrentUser,
    getHasPendingProgramApplication,
    getIsCurrentOrHasCompletedProgram,
    getOfficialTranscriptApproved,
    getOfficialTranscriptApprovedOrWaived,
    getRecordsIndicateUserShouldProvideTranscripts,
    getRelevantUserProgramStates,
} from 'Users';
import { type TranscriptViewConfig } from './editDocuments.types';

// The transcript section of the Documents tab renders in one of four shapes depending on where the
// learner is in the transcript process. This module is the whole decision, kept pure and free of
// React so it can be unit-tested against synthetic users — the four shapes cannot all be reached
// from a single real account.
//
// Ported from the `transcriptView` getter and `transcriptViewConfig` object in the old
// edit_documents_dir.js.

export const TranscriptViewKey = {
    // Applicant with a pending application: encourage an early upload.
    getHeadStart: 'get_head_start',
    // Accepted/current learner: only the degree needing an official transcript is shown, read-only.
    onlyAccepted: 'only_accepted',
    // Official transcripts are done — just a confirmation message.
    officialApprovedOrWaived: 'official_approved_or_waived',
    // Everyone else who should provide transcripts.
    officialNotApprovedOrWaived: 'official_not_approved_or_waived',
} as const;

const KEY_PREFIX = 'settings.edit_documents';

function educationExperiencesForUser(user: CurrentUser) {
    return educationExperiencesThatCanRequireOfficial(user.careerProfile?.educationExperiences ?? []);
}

const getHeadStart: TranscriptViewConfig = {
    key: TranscriptViewKey.getHeadStart,
    headerKey: `${KEY_PREFIX}.transcript_upload_section_title`,
    subTextKeys: [
        `${KEY_PREFIX}.unofficial_transcripts_description_part_one`,
        `${KEY_PREFIX}.unofficial_transcripts_description_part_two`,
    ],
    showBody: true,
    showFooter: true,
    educationExperiences: educationExperiencesForUser,
    educationExperienceSubTextKey: experience =>
        unofficialTranscripts(experience).length === 0 ? `${KEY_PREFIX}.unofficial_transcripts_upload` : null,
    showUnofficialTranscriptList: experience => unofficialTranscripts(experience).length > 0,
    showUploadButton: () => true,
};

const officialNotApprovedOrWaived: TranscriptViewConfig = {
    key: TranscriptViewKey.officialNotApprovedOrWaived,
    headerKey: `${KEY_PREFIX}.transcript_upload_section_title`,
    subTextKeys: [
        `${KEY_PREFIX}.unofficial_transcripts_please_upload`,
        `${KEY_PREFIX}.official_transcripts_also_required`,
    ],
    showBody: true,
    showFooter: false,
    educationExperiences: educationExperiencesForUser,
    educationExperienceSubTextKey: experience => {
        if (experience.officialTranscriptRequired) return `${KEY_PREFIX}.official_transcripts_required`;
        if (unofficialTranscripts(experience).length === 0) return `${KEY_PREFIX}.unofficial_transcripts_upload`;
        return null;
    },
    showUnofficialTranscriptList: experience => unofficialTranscripts(experience).length > 0,
    showUploadButton: experience =>
        experience.officialTranscriptRequired ? !experience.unofficialTranscriptsApproved : true,
};

const onlyAccepted: TranscriptViewConfig = {
    key: TranscriptViewKey.onlyAccepted,
    headerKey: `${KEY_PREFIX}.transcript_upload_section_title`,
    subTextKeys: [],
    showBody: true,
    showFooter: false,
    // Only the single degree that requires an official transcript, unlike the other views.
    educationExperiences: user => {
        const experience = educationExperienceRequiringOfficialTranscript(
            user.careerProfile?.educationExperiences ?? [],
        );
        return experience ? [experience] : [];
    },
    educationExperienceSubTextKey: () => `${KEY_PREFIX}.official_transcripts_required`,
    showUnofficialTranscriptList: () => false,
    showUploadButton: () => false,
};

function officialApprovedOrWaived(user: CurrentUser): TranscriptViewConfig {
    return {
        key: TranscriptViewKey.officialApprovedOrWaived,
        headerKey: `${KEY_PREFIX}.transcript_upload_section_title`,
        subTextKeys: [
            getOfficialTranscriptApproved(user)
                ? `${KEY_PREFIX}.official_transcripts_approved`
                : `${KEY_PREFIX}.official_transcripts_waived`,
        ],
        showBody: false,
        showFooter: false,
        educationExperiences: () => [],
        educationExperienceSubTextKey: () => null,
        showUnofficialTranscriptList: () => false,
        showUploadButton: () => false,
    };
}

// The program state that drives the transcript section: the first relevant one whose program type
// supports document upload, matching the filter `getTranscriptViewConfig` uses below. Callers need
// it to resolve brand-specific values (the registrar address) against the right program.
export function transcriptSectionUserProgramState(user: CurrentUser) {
    return (
        getRelevantUserProgramStates(user).find(
            ups => !!ups.programType && !!getProgramConfigValue(ups.programType, 'supportsDocumentUpload'),
        ) ?? null
    );
}

// Returns the config for the view this user should see, or null when the transcript section should
// not render at all. Selection order matches the old `transcriptView` getter.
export function getTranscriptViewConfig(user: CurrentUser): TranscriptViewConfig | null {
    if (!getRecordsIndicateUserShouldProvideTranscripts(user)) return null;

    if (getOfficialTranscriptApprovedOrWaived(user)) return officialApprovedOrWaived(user);

    // Note this reads the user program state's own programType, not the AOI record's — matching
    // `Cohort.supportsDocumentUpload(ups.programType)` in the old getter. (The user-level
    // `getRecordsIndicateUserShouldProvideTranscripts` check above does use the AOI record's, also
    // matching its Angular counterpart.)
    const documentUploadUserProgramStates = getRelevantUserProgramStates(user).filter(
        ups => !!ups.programType && !!getProgramConfigValue(ups.programType, 'supportsDocumentUpload'),
    );

    if (
        documentUploadUserProgramStates.some(ups => getIsCurrentOrHasCompletedProgram(user, { userProgramState: ups }))
    ) {
        return onlyAccepted;
    }

    if (documentUploadUserProgramStates.some(ups => getHasPendingProgramApplication(user, { userProgramState: ups }))) {
        return getHeadStart;
    }

    return officialNotApprovedOrWaived;
}

// The rows are ordered so degrees needing an official transcript come first, matching the old
// template's `orderBy: '-official_transcript_required'`.
export function sortEducationExperiencesForDisplay(
    experiences: CamelCasedEducationExperience[],
): CamelCasedEducationExperience[] {
    return [...experiences].sort((a, b) => Number(b.officialTranscriptRequired) - Number(a.officialTranscriptRequired));
}
